import type {
  AccessCode,
  AccessCodeMetadata,
  PermissionGroupMetadata
} from '@gat-ship/types'
import { accessCodes } from '@gat-ship/types'
import {
  IconAdministration as IconAdministrationRaw,
  IconAgencyStructure as IconAgencyStructureRaw,
  IconAi as IconAiRaw,
  IconStakeholders as IconStakeholdersRaw,
  IconSupport as IconSupportRaw,
  type IconComponent
} from '@gat-ship/icons'

// Re-declare with the broad IconComponent type so the inferred type of
// this file's exports doesn't leak a `lucide-vue-next/dist/...` path
// through the monorepo.
const IconAdministration: IconComponent = IconAdministrationRaw
const IconAgencyStructure: IconComponent = IconAgencyStructureRaw
const IconAi: IconComponent = IconAiRaw
const IconStakeholders: IconComponent = IconStakeholdersRaw
const IconSupport: IconComponent = IconSupportRaw

/**
 * The metadata for each access code.
 */
export const accessCodeMetadata: Record<AccessCode, AccessCodeMetadata> = {
  'S:AgencyStructure:View': {
    title: 'View Agency Structure',
    description:
      'Allows the user to view the complete agency hierarchy, including groups and agencies.',
    addedWhenPermissionsAreGranted: ['S:AgencyStructure:View'],
    removedWhenPermissionIsRemoved: ['S:AgencyStructure:View']
  },
  'S:SupportSession:Create': {
    title: 'Start Support Session',
    description: `Allows the user to grant the GAT-Ship support team time-limited access to this database for troubleshooting. 
    
    Support will impersonate the granting user and inherit the same roles and agency access.`,
    addedWhenPermissionsAreGranted: ['S:SupportSession:Create'],
    removedWhenPermissionIsRemoved: ['S:SupportSession:Create']
  },
  'S:SupportSession.TenantAdmin:Grant': {
    title: 'Grant Support Session with Tenant Admin Privileges',
    description: `Allows the user to grant Gatship support team full tenant-admin rights for a session, enabling all administrative actions regardless of the user's own role.`,
    privilegeLevel: 'high',
    requiresPermissions: ['S:SupportSession:Create'],
    addedWhenPermissionsAreGranted: ['S:SupportSession.TenantAdmin:Grant'],
    removedWhenPermissionIsRemoved: ['S:SupportSession.TenantAdmin:Grant']
  },
  'S:Currencies:Manage': {
    title: 'Manage Available Currencies',
    description:
      'Allows the user to choose which currencies are available for use in the system',
    addedWhenPermissionsAreGranted: ['S:Currencies:Manage'],
    removedWhenPermissionIsRemoved: ['S:Currencies:Manage']
  },
  'S:Stakeholder:View': {
    title: 'Access Stakeholders',
    description: `Allows the user to access the stakeholder registries (Clients, Vendors and Other parties).  
    
    Users without this permission can still select existing stakeholders elsewhere in the system.`,
    addedWhenPermissionsAreGranted: ['S:Stakeholder:View'],
    removedWhenPermissionIsRemoved: [
      'S:Stakeholder:View',
      'S:Stakeholder:Add',
      'S:Stakeholder:Modify',
      'S:Stakeholder.Contact:View',
      'S:Stakeholder.Contact:Add',
      'S:Stakeholder.Contact:Modify',
      'S:Stakeholder.Contact:Remove',
      'S:Stakeholder.SystemReferenceNumbers:Modify',
      'S:Stakeholder.BusinessNumbers:Modify',
      'S:Stakeholder.BillingDetails:Modify'
    ]
  },
  'S:Stakeholder:Add': {
    title: 'Add and Modify Stakeholder',
    description: undefined,
    requiresPermissions: ['S:Stakeholder:View'],
    addedWhenPermissionsAreGranted: [
      'S:Stakeholder:Add',
      'S:Stakeholder:Modify'
    ],
    removedWhenPermissionIsRemoved: [
      'S:Stakeholder:Add',
      'S:Stakeholder:Modify',
      'S:Stakeholder.SystemReferenceNumbers:Modify',
      'S:Stakeholder.BusinessNumbers:Modify',
      'S:Stakeholder.BillingDetails:Modify',
      'S:Stakeholder.Contact:View',
      'S:Stakeholder.Contact:Add',
      'S:Stakeholder.Contact:Modify',
      'S:Stakeholder.Contact:Remove'
    ]
  },
  'S:Stakeholder:Modify': {
    title: 'Modify Stakeholder',
    description: undefined,
    requiresPermissions: ['S:Stakeholder:View'],
    addedWhenPermissionsAreGranted: [
      'S:Stakeholder:Modify',
      'S:Stakeholder.Contact:View',
      'S:Stakeholder.Contact:Add',
      'S:Stakeholder.Contact:Modify'
    ],
    removedWhenPermissionIsRemoved: [
      'S:Stakeholder:Modify',
      'S:Stakeholder.Contact:View',
      'S:Stakeholder.Contact:Add',
      'S:Stakeholder.Contact:Modify',
      'S:Stakeholder.Contact:Remove',
      'S:Stakeholder.SystemReferenceNumbers:Modify',
      'S:Stakeholder.BusinessNumbers:Modify',
      'S:Stakeholder.BillingDetails:Modify'
    ],
    hiddenWhenPermissionsAreGranted: ['S:Stakeholder:Add']
  },
  'S:Stakeholder.SystemReferenceNumbers:Modify': {
    title: 'Modify Stakeholder System Reference Numbers',
    description:
      'Allows the user to edit system reference numbers for stakeholders (e.g., client/vendor number)',
    requiresPermissions: ['S:Stakeholder:View', 'S:Stakeholder:Modify'],
    addedWhenPermissionsAreGranted: [
      'S:Stakeholder.SystemReferenceNumbers:Modify'
    ],
    removedWhenPermissionIsRemoved: [
      'S:Stakeholder.SystemReferenceNumbers:Modify'
    ]
  },
  'S:Stakeholder.BusinessNumbers:Modify': {
    title: 'Modify Stakeholder Business Numbers',
    description:
      'Allows the user to edit official business/registration numbers for stakeholders (e.g., VAT, EORI).',
    requiresPermissions: ['S:Stakeholder:View', 'S:Stakeholder:Modify'],
    addedWhenPermissionsAreGranted: ['S:Stakeholder.BusinessNumbers:Modify'],
    removedWhenPermissionIsRemoved: ['S:Stakeholder.BusinessNumbers:Modify']
  },
  'S:Stakeholder.BillingDetails:Modify': {
    title: 'Modify Client Billing Details',
    description:
      'Allows the user to edit client billing settings, such as invoice terms and credit limits.',
    requiresPermissions: ['S:Stakeholder:View', 'S:Stakeholder:Modify'],
    addedWhenPermissionsAreGranted: ['S:Stakeholder.BillingDetails:Modify'],
    removedWhenPermissionIsRemoved: ['S:Stakeholder.BillingDetails:Modify']
  },
  'S:Stakeholder.Contact:View': {
    title: 'View Stakeholder Contact',
    description: undefined,
    addedWhenPermissionsAreGranted: ['S:Stakeholder.Contact:View'],
    removedWhenPermissionIsRemoved: ['S:Stakeholder.Contact:View']
  },
  'S:Stakeholder.Contact:Add': {
    title: 'Add Stakeholder Contact',
    description: undefined,
    requiresPermissions: ['S:Stakeholder:View', 'S:Stakeholder.Contact:View'],
    addedWhenPermissionsAreGranted: ['S:Stakeholder.Contact:Add'],
    removedWhenPermissionIsRemoved: ['S:Stakeholder.Contact:Add']
  },
  'S:Stakeholder.Contact:Modify': {
    title: 'Modify Stakeholder Contact',
    description: undefined,
    requiresPermissions: ['S:Stakeholder:View', 'S:Stakeholder.Contact:View'],
    addedWhenPermissionsAreGranted: ['S:Stakeholder.Contact:Modify'],
    removedWhenPermissionIsRemoved: ['S:Stakeholder.Contact:Modify']
  },
  'S:Stakeholder.Contact:Remove': {
    title: 'Remove Stakeholder Contact',
    description: undefined,
    requiresPermissions: ['S:Stakeholder:View', 'S:Stakeholder:Modify'],
    addedWhenPermissionsAreGranted: ['S:Stakeholder.Contact:Remove'],
    removedWhenPermissionIsRemoved: ['S:Stakeholder.Contact:Remove']
  },
  'A:Agency:View': {
    title: 'View Agency Details',
    description:
      'Allows the user to view all stored details for an agency, including name, address, and linked company information.',
    addedWhenPermissionsAreGranted: ['A:Agency:View'],
    removedWhenPermissionIsRemoved: ['A:Agency:View']
  },
  'A:Agency.ContactInfo:Change': {
    title: 'Modify Agency Contact Info',
    description: `Allows the user to update an agency's contact details (address, phone, email) within Administration.`,
    addedWhenPermissionsAreGranted: [
      'A:Agency.ContactInfo:Change',
      'A:Agency:View',
      'S:AgencyStructure:View'
    ],
    removedWhenPermissionIsRemoved: [
      'A:Agency.ContactInfo:Change',
      'A:Agency:View',
      'S:AgencyStructure:View'
    ]
  },
  'S:GlobalSettingsView:Access': {
    title: 'View Global Settings',
    description: 'Allows the user to view the global settings.',
    addedWhenPermissionsAreGranted: ['S:GlobalSettingsView:Access'],
    removedWhenPermissionIsRemoved: ['S:GlobalSettingsView:Access']
  },
  'S:AgencySettingsView:Access': {
    title: 'View Agency Settings',
    description: 'Allows the user to view the agency settings.',
    addedWhenPermissionsAreGranted: ['S:AgencySettingsView:Access'],
    removedWhenPermissionIsRemoved: ['S:AgencySettingsView:Access']
  },
  'S:AI.PromptLibrary:Manage': {
    title: 'Manage AI Prompt Library',
    description: 'Allows the user to manage the AI prompt library.',
    addedWhenPermissionsAreGranted: ['S:AI.PromptLibrary:Manage'],
    removedWhenPermissionIsRemoved: ['S:AI.PromptLibrary:Manage']
  },
  'S:AI.ModelForRequest:Modify': {
    title: 'Override AI Model',
    description: 'Allows the user to override the AI model.',
    addedWhenPermissionsAreGranted: ['S:AI.ModelForRequest:Modify'],
    removedWhenPermissionIsRemoved: ['S:AI.ModelForRequest:Modify']
  }
}

/**
 * The permission groups - as represented in the UI.
 * Each group has an icon and a list of permission items.
 * @property icon - The icon of the group.
 * @property description (optional) - The description of the group.
 * @property permissionItems - The access codes that are shown in the group.
 */
export const permissionGroups: Record<string, PermissionGroupMetadata> = {
  Administration: {
    icon: IconAdministration,
    permissionItems: [
      /* accessCodes.system.views.globalSettings,
      accessCodes.system.views.agencySettings, */
      accessCodes.system.currency.manageCurrencies
    ]
  },
  Organization: {
    icon: IconAgencyStructure,
    permissionItems: [
      /* accessCodes.system.agency.viewAgencyStructure,
      accessCodes.administration.agency.view, */
      accessCodes.administration.agency.changeContactInfo // -> Skal gi accessCodes.system.agency.viewAgencyStructure + accessCodes.administration.agency.view
    ]
  },
  Support: {
    icon: IconSupport,
    permissionItems: [
      accessCodes.system.support.createSupportSession,
      accessCodes.system.support.createSupportSessionWithTenantAdminPrivileges
    ]
  },
  Stakeholders: {
    icon: IconStakeholders,
    permissionItems: [
      accessCodes.system.stakeholder.accessStakeholders,
      accessCodes.system.stakeholder.addStakeholder,
      accessCodes.system.stakeholder.modifyStakeholder,
      accessCodes.system.stakeholder.modifySystemReferenceNumbers,
      accessCodes.system.stakeholder.modifyBusinessNumbers,
      accessCodes.system.stakeholder.modifyBillingDetails,
      /* accessCodes.system.stakeholder.viewContact,
      accessCodes.system.stakeholder.addContact,
      accessCodes.system.stakeholder.modifyContact, */
      accessCodes.system.stakeholder.removeContact
    ]
  },
  AI: {
    icon: IconAi,
    permissionItems: [
      accessCodes.system.aiPromptLibrary.manage,
      accessCodes.system.aiPromptLibrary.overrideModelForRequest
    ]
  }
  // Monetary: {
  //   icon: IconCurrency,
  //   permissionItems: [accessCodes.system.currency.manageCurrencies]
  // },
}
